import { Component } from '@angular/core';
import { QueryService } from '@pt/api-client';
import { ActionsSubject, Store } from '@ngrx/store';
import { AbstractWidgetDirective } from '../../abstract-widget.directive';
import { UiWidget, WidgetConfiguration } from '@pt/pt-models';
import { TranslateService } from '@ngx-translate/core';
import { WidgetDialogService } from '../../../services/widget-dialog.service';
import { NgxPermissionsService } from 'ngx-permissions';
import * as NgrxWidgetSelectors from '../../../store/widget.selectors';
import { ValidatorGenerator } from '../../../utils/validator-generator';
import { WidgetDateOutput } from './widget-date-v0-edit.component';

@Component({
  selector: 'pt-widget-date-v0',
  template: `
    <div class='position-relative' *ngIf="internalStatus.enabled">

      <pt-widget-disabled
        *ngIf='internalStatus.isDisabled'
        [showLoader]="internalStatus.viewMode === 'LOADING'">
      </pt-widget-disabled>

      <pt-widget-header
        class='w-100'
        [label]='widget?.label'
        [help]='widget?.help'

        [comment]="comment"
        [unsavedComment]="unsavedComment"
        [showCancelButton]='internalStatus.showCancelButton'
        [showCommentButton]='internalStatus.showCommentButton'
        [showEditButton]='internalStatus.showEditButton'
        [showReloadButton]='internalStatus.showReloadButton'
        [showSaveButton]='internalStatus.showSaveButton'
        [showOptButton]='internalStatus.showOptButton'

        [disabledCancelButton]='internalStatus.disabledCancelButton'
        [disabledCommentButton]='internalStatus.disabledCommentButton'
        [disabledEditButton]='internalStatus.disabledEditButton'
        [disabledOptButton]='internalStatus.disabledOptButton'
        [disabledReloadButton]='internalStatus.disabledReloadButton'
        [disabledSaveButton]='internalStatus.disabledSaveButton'
        [loading]='internalStatus.isLoading'

        (clickSaveWidget)='saveWidget()'
        (clickResetWidget)='resetWidget(widget)'
        (clickEditWidget)='dispatchEditState()'
        (clickReloadWidget)='reloadWidget(widget)'
        (clickCreateOpt)='createOptWidget()'
        (clickCommentWidget)='clickCommentWidget()'>
      </pt-widget-header>

      <pt-widget-date-v0-autosave
        *ngIf="internalStatus?.isAutoSave; else noAutosave"
        [disabled]="internalStatus.viewMode !== 'EDIT'"
        [value]='widgetValue'
        [noDataLabel]="noDataLabel"
        (changeDateAndSave)="changeDate($event)">
      </pt-widget-date-v0-autosave>

      <ng-template #noAutosave>

        <ng-container *ngIf='!internalStatus.showOptButton'>

          <pt-widget-date-v0-edit
            class="overflow-hidden"
            [show]="internalStatus.viewMode === 'EDIT' || internalStatus.viewMode === 'LOADING'"
            [date]='widgetValue'
            (changeDate)='changeDate($event)'>
          </pt-widget-date-v0-edit>

          <pt-widget-date-v0-preview
            *ngIf="internalStatus.viewMode === 'PREVIEW'"
            [noDataLabel]="noDataLabel"
            [currentLanguage]='currentLanguage$ | async'
            [previewData]='previewData'
            [loading]='internalStatus.isLoading'>
          </pt-widget-date-v0-preview>

          <pt-widget-error
            *ngIf="internalStatus.viewMode === 'ERROR'"
            [errorMessage]='internalStatus.errorMessage | async'>
          </pt-widget-error>

          <pt-widget-footer></pt-widget-footer>
        </ng-container>

      </ng-template>
    </div>
  `
})
export class WidgetDateV0Component extends AbstractWidgetDirective<number> {
  widgetValue: number;
  currentValue: number;
  public currentLanguage$ = this.store.select(NgrxWidgetSelectors.currentLanguage);
  private dateResponse?: WidgetDateOutput;

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              public translateService: TranslateService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
  }

  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();
    this.widgetValue = widget.value as number;
    this.currentValue = this.widgetValue;
    this.checkValidity();

    setTimeout(() => this.dispatchCompleteState(widget.value));
  }

  saveWidget(ignoreTrigger?: boolean): void {
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    widget.value = this.currentValue;
    widget.comment = this.comment;
    this.checkValidity();
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  changeDate($event: WidgetDateOutput) {
    this.dateResponse = $event;
    if (this.currentValue !== $event.timestamp) {
      this.currentValue = $event.timestamp;
      this.checkValidity();

      if (this.internalStatus.isAutoSave && !this.internalStatus.isLoading && this.internalStatus.isDraft) {
        this.saveWidget();
      }
    }
  }

  configuration(): WidgetConfiguration {
    return {supportAutoSave: true, supportValidation: true};
  }

  private checkValidity() {
    if (this.dateResponse && this.dateResponse.error) {
      this.isValid$.next(false);
    } else {
      if (this.widget?.opt) {
        this.isValid$.next(true);
      } else {
        if (ValidatorGenerator.validatorsContainsRequired(this.widget?.validators)) {
          this.isValid$.next(this.currentValue !== null && this.currentValue !== undefined);
        } else {
          this.isValid$.next(true);
        }
      }
    }

  }
}
